import { attr, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { ExecutionDrilldownTemplate as template } from './execution-drilldown.template';
import { ExecutionDrilldownStyles as styles } from './execution-drilldown.styles';
import { IArteriaNegotiation, IExecutionNegotiation } from '../../../../types';
import { CASCADE_ENUM, EnumTranslate, IEnumService } from '../../../../services/enum.service';
import { IStructuredChatService } from '../../../../services/structured-chat.service';
import { Auth, logger } from '@genesislcap/foundation-comms';
import {
  ARTERIA_ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS,
  CONTRACT_TYPE,
  FORM_AGREEMENT_TYPE,
  PROFILE_RIGHTS,
} from '../../../../utils';
import { INegotiationService } from '../../../../services';
import { notifyError } from '../../../../utils/notify';
import { IUserService } from '../../../../services/user.service';
import { IAutoExecuteApprovedCounterpartiesService } from '../../../../services/auto-execute-approved-counterparties.service';
import {IThirdPartyService} from "../../../../services/third-party.service";

const name = 'execution-drilldown';

@customElement({
  name,
  template,
  styles,
})
export class ExecutionDrilldown extends FASTElement {
  @IUserService userService: IUserService;
  @Auth auth: Auth;
  @IStructuredChatService structuredChatService: IStructuredChatService;
  @INegotiationService negotiationService: INegotiationService;
  @IThirdPartyService thirdPartyService: IThirdPartyService;
  @IEnumService enumService: IEnumService;
  @IAutoExecuteApprovedCounterpartiesService
  autoExecuteService: IAutoExecuteApprovedCounterpartiesService;

  @observable valueColumnRef: HTMLDivElement;
  @observable drilldownWrapper: HTMLDivElement;

  @observable applicableRECProgramEnum: Array<EnumTranslate>;
  @observable negativePriceMethodEnum: Array<EnumTranslate>;
  @observable swapSettlementCalcMethodEnum: Array<EnumTranslate>;
  @observable performanceGuaranteeMethodEnum: Array<EnumTranslate>;
  @observable settlementIndexEnum: Array<EnumTranslate>;
  @observable technologyEnum: Array<EnumTranslate>;
  @observable contractTypeEnum: Array<EnumTranslate>;
  @observable applicableTrackingSystemEnum: Array<EnumTranslate>;
  @observable recDeliveryMethodologyEnum:  Array<EnumTranslate>;

  @observable isAutoExecuted: boolean = false;

  @attr @observable negotiation = null;
  @attr @observable isNegotiationDetails = false;

  @observable arteriaNegotiation: IArteriaNegotiation = null;
  @observable autoExecutedNegotiation: IExecutionNegotiation = null;
  @observable canPostTrade: boolean = false; // Temporary boolean to grey out/disable "Post Trade" button until "Post Trade" is implemented

  public DEFAULT_ARTERIA_VALUE = 'As agreed per the Confirmation.';
  public DEFUALT_REC_VALUE = 'As agreed by the Parties';

  connectedCallback() {
    super.connectedCallback();
    // TODO: will probably need to remove the Enum services once the data integration fro artiria is done
    this.loadEnums();
  }

  private async loadEnums() {
    this.enumService.getEnumValues(CASCADE_ENUM.APPLICABLE_REC_PROGRAM).then((response) => {
      if (response.REPLY) {
        this.applicableRECProgramEnum = response.REPLY;
      }
    });
    this.enumService.getEnumValues(CASCADE_ENUM.NEGATIVE_PRICING_METHOD).then((response) => {
      if (response.REPLY) {
        this.negativePriceMethodEnum = response.REPLY;
      }
    });
    this.enumService
      .getEnumValues(CASCADE_ENUM.ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHOD)
      .then((response) => {
        if (response.REPLY) {
          this.swapSettlementCalcMethodEnum = response.REPLY;
        }
      });
    this.enumService.getEnumValues(CASCADE_ENUM.PERFORMANCE_GUARANTEE_METHOD).then((response) => {
      if (response.REPLY) {
        this.performanceGuaranteeMethodEnum = response.REPLY;
      }
    });
    this.enumService.getEnumValues(CASCADE_ENUM.SETTLEMENT_INDEX).then((response) => {
      if (response.REPLY) {
        this.settlementIndexEnum = response.REPLY;
      }
    });
    this.enumService.getEnumValues(CASCADE_ENUM.TECHNOLOGY).then((response) => {
      if (response.REPLY) {
        this.technologyEnum = response.REPLY;
      }
    });
    this.enumService.getFilteredEnumValues(CASCADE_ENUM.CONTRACT_TYPE).then((response) => {
      if (response.REPLY) {
        this.contractTypeEnum = response.REPLY;
      }
    });
    this.enumService.getEnumValues(CASCADE_ENUM.APPLICABLE_TRACKING_SYSTEM).then((response) => {
      if (response.REPLY) {
        this.applicableTrackingSystemEnum = response.REPLY;
      }
    });
    this.enumService.getEnumValues(CASCADE_ENUM.REC_DELIVERY_METHODOLOGY).then((response) => {
      if (response.REPLY) {
        this.recDeliveryMethodologyEnum = response.REPLY;
      }
    });
  }

  public getStatusClassIcon(isBuyer: boolean) {
    let statusClass = 'confirmed';

    if (this.negotiation && this.negotiation.NEGOTIATION_STATUS) {
      switch (this.negotiation.NEGOTIATION_STATUS) {
        case 'PENDING_EXECUTION': {
          statusClass = 'awaiting-confirmation';
          break;
        }
        case 'PENDING_EXECUTION_TIMER': {
          statusClass = 'awaiting-confirmation';
          break;
        }
        case 'EXECUTED_AWAITING_BUYER': {
          statusClass = isBuyer ? 'awaiting-confirmation' : 'confirmed';
          break;
        }
        case 'EXECUTED_AWAITING_SELLER': {
          statusClass = isBuyer ? 'confirmed' : 'awaiting-confirmation';
          break;
        }
        case 'EXPIRED': {
          statusClass = '';
          break;
        }
      }
    }

    return statusClass;
  }

  public disableExecuteBtn() {
    let disableExecution = true;

    if (this.negotiation && this.negotiation.NEGOTIATION_STATUS) {
      if (
        this.userService.getFilteredPermissions().includes(PROFILE_RIGHTS.BUYER_VISIBILITY) &&
        this.negotiation.NEGOTIATION_STATUS === 'EXECUTED_AWAITING_BUYER'
      ) {
        disableExecution = false;
      }

      if (
        this.userService.getFilteredPermissions().includes(PROFILE_RIGHTS.SELLER_VISIBILITY) &&
        this.negotiation.NEGOTIATION_STATUS === 'EXECUTED_AWAITING_SELLER'
      ) {
        disableExecution = false;
      }

      if (this.negotiation.NEGOTIATION_STATUS === 'PENDING_EXECUTION') {
        disableExecution = false;
      }

      if (this.negotiation.NEGOTIATION_STATUS === 'PENDING_EXECUTION_TIMER') {
        disableExecution = true;
      }
    }

    return disableExecution;
  }

  public executeNegotiation() {
    this.negotiationService.eventNegotiationExecute(this.negotiation.NEGOTIATION_ID).then(
      (response) => {
        if (response && response.MESSAGE_TYPE === 'EVENT_ACK') {
        } else {
          notifyError(response.ERROR[0].TEXT);
          logger.error(response.ERROR);
        }
      },
      (error) => {
        notifyError(error);
        logger.error(error);
      }
    );
  }

  public reopenNegotiationConfirm() {
    this.$emit('reopenNegotiationConfirm', this.negotiation);
  }

  public isExpiredOrDeleted(status) {
    return ['EXPIRED', 'BUYER_DELETED', 'SELLER_DELETED', 'FULL_DELETED'].includes(status);
  }

  public negotiationChanged() {
    if (this.negotiation) {
      this.isAutoExecuted = this.isNegotiationDetails
        ? this.negotiation.VPPA_CONTRACT_TYPE === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED
        : !this.negotiation.hasOwnProperty('TICKER');

      if (this.isAutoExecuted) {
        this.autoExecutedNegotiation = this.negotiation;
      } else {
        this.arteriaNegotiation = this.negotiation;
      }
      setTimeout(() => {
        const truncateItems = this.valueColumnRef.childNodes;
        truncateItems.forEach((item) => {
          if (item.nodeName === 'DIV') {
            item['title'] = item.textContent;
          }
        });
        this.drilldownWrapper.scrollTo({ top: 0 });
      }, 100);
    }
  }

  public showValueByArteriaSwapValue(
    arteriaSwapValue: ARTERIA_ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS
  ): boolean {
    if (
      !this.arteriaNegotiation?.ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS ||
      !this.validateArteriaSwapString()
    ) {
      return true;
    } else if (
      this.arteriaNegotiation.ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS === arteriaSwapValue
    ) {
      return true;
    }

    return false;
  }

  public goToArteriaNegotiationsPage() {
    const projectUniqueIdentifier = this.negotiation.PROJECT_ID;
    if (projectUniqueIdentifier) {
      this.thirdPartyService.openArteriaChecklist(projectUniqueIdentifier);
    } else {
      this.thirdPartyService.openArteriaNegotiations();
    }
  }

  public validateArteriaSwapString(): boolean {
    for (const key in ARTERIA_ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS) {
      if (
        ARTERIA_ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS[key] ===
        this.arteriaNegotiation?.ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS
      ) {
        return true;
      }
    }
    return false;
  }

  getDefaultValueIfInvalidSwap(fieldValue: string): string {
    if (!this.validateArteriaSwapString()) {
      return this.DEFAULT_ARTERIA_VALUE;
    }

    return fieldValue;
  }

  getDefaultValueIfBlank(fieldValue: string): string {
    if (!fieldValue) {
      return this.arteriaNegotiation?.FORM_AGREEMENT_TYPE === FORM_AGREEMENT_TYPE.OFF_PLATFORM_REC
        ? this.DEFUALT_REC_VALUE
        : this.DEFAULT_ARTERIA_VALUE;
    }

    return fieldValue;
  }

  getRatio(numerator: number, denominator: number): number {
    if (denominator != 0 || denominator != null || numerator != null) {
      return (numerator / denominator) * 100;
    }
  }

  public getNotionalWarningMsg() {
    const missingFields = [];
    if(!this.arteriaNegotiation.EXECUTION_FIXED_PRICE) {
      missingFields.push('Fixed Price')
    }
    if(!this.arteriaNegotiation.EXECUTION_TENOR_START_DATE) {
      if(this.arteriaNegotiation.CONTRACT_TYPE === CONTRACT_TYPE.NEW_BUILD_VPPA) {
        missingFields.push('Target Commercial Operation Date')
      } else {
        missingFields.push('Tenor Start Date')
      }
    }
    if(!this.arteriaNegotiation.EXECUTION_TENOR_MONTHS) {
      missingFields.push('Tenor (months)')
    }
    if(!this.arteriaNegotiation.EXECUTION_BUYERS_FRACTION) {
      missingFields.push('Buyer\'s Fraction')
    }
    if(!this.arteriaNegotiation.REPORTING_COUNTERPARTY) {
      missingFields.push('Reporting Counterparty')
    }
    if(!this.arteriaNegotiation.EXECUTION_NAMEPLATE_CAPACITY) {
      if(this.arteriaNegotiation.CONTRACT_TYPE === CONTRACT_TYPE.NEW_BUILD_VPPA) {
        missingFields.push('Expected Nameplate Capacity')
      } else {
        missingFields.push('Installed Nameplate Capacity')
      }
    }
    let pluralSingular = 'This field is';
    if(missingFields.length > 1) {
      pluralSingular = 'These fields are'
    }
    return  'Failure to calculate Notional Value of Swap due to a missing ' +
        `or improperly formatted value in ${missingFields.join(', ')}. ` +
        `${pluralSingular} required for Dodd-Frank and CFTC reporting. Please ` +
        'ensure that your Contract contains values for Fixed Price, Tenor ' +
        'Start Date, Tenor (months), Buyer\'s Fraction, and Nameplate Capacity in the correct format as specified. ' +
        'For additional assistance, contact us at markets@resurety.com.'
  }
}
